import { mergeAbortSignals } from "./hubQueryAbortSignal";
import {
  resourceTimingForUrl,
  updateApiFetchTimeline,
  type ApiFetchTimeline,
} from "./apiFetchTelemetry";
import { registerTabWakeStaleFetch } from "./tabWakeStaleFetch";

export const DEFAULT_REST_HEADER_DEADLINE_MS = 10_000;
export const DEFAULT_REST_TOTAL_DEADLINE_MS = 30_000;

export type ApiFetchDeadlineOptions = {
  headerDeadlineMs: number;
  totalDeadlineMs: number;
};

export class ApiDeadlineError extends Error {
  readonly name = "ApiDeadlineError";

  constructor(
    readonly phase: "before-headers" | "after-headers",
    readonly elapsedMs: number,
    readonly headerDeadlineMs: number,
    readonly totalDeadlineMs: number,
    readonly nextHopProtocol: string | null,
    readonly responseStart: number | null,
    readonly callerAborted: boolean,
  ) {
    super(
      phase === "before-headers"
        ? "The hub did not begin responding before the header deadline."
        : "The hub response did not finish before the total deadline.",
    );
  }
}

export type FetchWithDeadlineResult = {
  response: Response;
  text: string;
};

export async function fetchWithDeadlines(
  url: string,
  init: RequestInit,
  deadlines: ApiFetchDeadlineOptions | null,
  callerSignal: AbortSignal | undefined,
  timeline: ApiFetchTimeline,
): Promise<FetchWithDeadlineResult> {
  const startedAtMs = Date.now();
  const deadlineController = new AbortController();
  let ownDeadlinePhase: "before-headers" | "after-headers" | null = null;
  let ownDeadlineOutcome: "header-deadline" | "total-deadline" | null = null;
  let headersReceived = false;
  let headerTimer: ReturnType<typeof globalThis.setTimeout> | undefined;
  let totalTimer: ReturnType<typeof globalThis.setTimeout> | undefined;

  if (deadlines != null) {
    headerTimer = globalThis.setTimeout(() => {
      ownDeadlinePhase = "before-headers";
      ownDeadlineOutcome = "header-deadline";
      updateApiFetchTimeline(timeline, { headerDeadlineFiredAt: Date.now() });
      deadlineController.abort("header-deadline");
    }, deadlines.headerDeadlineMs);
    totalTimer = globalThis.setTimeout(() => {
      ownDeadlinePhase = headersReceived ? "after-headers" : "before-headers";
      ownDeadlineOutcome = "total-deadline";
      updateApiFetchTimeline(timeline, { totalDeadlineFiredAt: Date.now() });
      deadlineController.abort("total-deadline");
    }, deadlines.totalDeadlineMs);
  }

  const signal = mergeAbortSignals(callerSignal, deadlineController.signal);
  const unregisterTabWake = registerTabWakeStaleFetch({
    startedAtMs,
    headerDeadlineMs: deadlines?.headerDeadlineMs ?? DEFAULT_REST_HEADER_DEADLINE_MS,
    hasHeaders: () => headersReceived,
    abort: () => {
      if (headersReceived) return;
      ownDeadlinePhase = "before-headers";
      ownDeadlineOutcome = "header-deadline";
      updateApiFetchTimeline(timeline, { headerDeadlineFiredAt: Date.now() });
      deadlineController.abort("header-deadline");
    },
  });
  try {
    updateApiFetchTimeline(timeline, { fetchCalledAt: startedAtMs });
    const response = await fetch(url, { ...init, signal });
    headersReceived = true;
    if (headerTimer != null) globalThis.clearTimeout(headerTimer);
    updateApiFetchTimeline(timeline, { headersReceivedAt: Date.now() });
    const text = await response.text();
    updateApiFetchTimeline(timeline, {
      bodyCompletedAt: Date.now(),
      outcome: "completed",
      ...resourceTimingForUrl(url),
    });
    return { response, text };
  } catch (error) {
    if (ownDeadlinePhase != null && callerSignal?.aborted !== true) {
      const timing = resourceTimingForUrl(url);
      updateApiFetchTimeline(timeline, {
        ...timing,
        outcome: ownDeadlineOutcome ?? "error",
      });
      throw new ApiDeadlineError(
        ownDeadlinePhase,
        Date.now() - startedAtMs,
        deadlines?.headerDeadlineMs ?? DEFAULT_REST_HEADER_DEADLINE_MS,
        deadlines?.totalDeadlineMs ?? DEFAULT_REST_TOTAL_DEADLINE_MS,
        timing.nextHopProtocol,
        timing.responseStart,
        false,
      );
    }
    if (callerSignal?.aborted) {
      updateApiFetchTimeline(timeline, {
        callerAbortedAt: Date.now(),
        outcome: "caller-aborted",
      });
    } else {
      updateApiFetchTimeline(timeline, { outcome: "error" });
    }
    throw error;
  } finally {
    unregisterTabWake();
    if (headerTimer != null) globalThis.clearTimeout(headerTimer);
    if (totalTimer != null) globalThis.clearTimeout(totalTimer);
  }
}
